import { PermissionType } from './permission-type.type'

type KeysPermissionsSource = keyof typeof PermissionsSource
type ValuesPermissionsSource = (typeof PermissionsSource)[KeysPermissionsSource]

export type PermissionSourceType = PermissionType | PermissionSrc | ValuesPermissionsSource

export enum PermissionSrc {
  VEHICLE_LIST = 'vehicle_list',
  DEVICE_BY_ID = 'device_by_id',
}

export interface PermissionStructureModel {
  [key: string]: PermissionStructureModel | null
}

export enum SelectState {
  Empty,
  Partial,
  Checked,
}

export class TodoItemNode {
  children: TodoItemNode[]
  level: number
  label: string
  value: string
  labelVariable?: string
  selectedNumber: SelectState
}

export interface TodoItemFlatNodeProps {
  level?: number
  expandable?: boolean
  label?: string
  value?: string
  labelVariable?: string
  selectedNumber?: SelectState
  disabled?: boolean
}

export class TodoItemFlatNode {
  level: number
  expandable: boolean
  label: string
  value: string
  selectedNumber = SelectState.Empty
  disabled: boolean
  labelVariable?: string

  constructor(props: TodoItemFlatNodeProps) {
    this.level = props.level
    this.expandable = props.expandable
    this.label = props.label
    this.value = props.value
    this.selectedNumber = props.selectedNumber
    this.disabled = props.disabled
    this.labelVariable = props.labelVariable
  }
}

export const CamerasPermissions = [
  'cameras.channels.channel_outward',
  'cameras.channels.channel_dms',
  'cameras.channels.channel_ext1',
  'cameras.channels.channel_ext2',
  'cameras.channels.channel_ext3',
  'cameras.channels.channel_ext4',
]

export const PermissionsSource = {
  ALERTS: 'alerts',
  ASSET: 'asset',
  'ASSET.ASSETS_LIST': 'asset.assets_list',
  'ASSET.ASSETS_LIST.ADD': 'asset.assets_list.add',
  'ASSET.ASSETS_LIST.DELETE': 'asset.assets_list.delete',
  'ASSET.ASSETS_LIST.EDIT': 'asset.assets_list.edit',
  'ASSET.ASSETS_LIST.INSTALL_UNINSTALL': 'asset.assets_list.install_uninstall',
  'ASSET.DOCUMENTS_LIST': 'asset.documents_list',
  'ASSET.DOCUMENTS_LIST.ADD': 'asset.documents_list.add',
  'ASSET.DOCUMENTS_LIST.DELETE': 'asset.documents_list.delete',
  'ASSET.DOCUMENTS_LIST.ARCHIVE': 'asset.documents_list.archive',
  'ASSET.DOCUMENTS_LIST.EDIT': 'asset.documents_list.edit',
  'ASSET.DOCUMENTS_LIST.RECORDS_LIST': 'asset.documents_list.records_list',
  'ASSET.DOCUMENTS_LIST.RECORDS_LIST.ADD': 'asset.documents_list.records_list.add',
  'ASSET.SERVICE_REMINDERS_LIST': 'asset.service_reminders_list',
  'ASSET.SERVICE_REMINDERS_LIST.ADD': 'asset.service_reminders_list.add',
  'ASSET.SERVICE_REMINDERS_LIST.DELETE': 'asset.service_reminders_list.delete',
  'ASSET.SERVICE_REMINDERS_LIST.ARCHIVE': 'asset.service_reminders_list.archive',
  'ASSET.SERVICE_REMINDERS_LIST.EDIT': 'asset.service_reminders_list.edit',
  'ASSET.SERVICE_REMINDERS_LIST.RECORDS_LIST': 'asset.service_reminders_list.records_list',
  'ASSET.SERVICE_REMINDERS_LIST.RECORDS_LIST.ADD': 'asset.service_reminders_list.records_list.add',
  'ASSET.SERVICE_REMINDERS_LIST.RECORDS_LIST.EDIT': 'asset.service_reminders_list.records_list.edit',
  'ASSET.SERVICE_REMINDERS_LIST.RECORDS_LIST.DELETE': 'asset.service_reminders_list.records_list.delete',
  BILLING: 'billing',
  'BILLING.INVOICE_PAY': 'billing.invoice_pay',
  'BILLING.PAYMENT_CHANGE': 'billing.payment_change',
  'BILLING.INVOICE_VIEW': 'billing.invoice_view',
  'BILLING.CLEAN': 'billing.clean',
  'BILLING_PLAN.EDIT': 'billing_plan.edit',
  CAMERAS: 'cameras',
  'CAMERAS.CHANNELS': 'cameras.channels',
  'CAMERAS.WAKE_DEVICE': 'cameras.wake_device',
  'CAMERAS.CAMERAS_MODULE': 'cameras.cameras_module',
  'CAMERAS.CAMERAS_MODULE.CONFIGURATION': 'cameras.cameras_module.configuration',
  'CAMERAS.CAMERAS_MODULE.ARCHIVE': 'cameras.cameras_module.archive',
  'CAMERAS.CAMERAS_MODULE.EVENTS': 'cameras.cameras_module.events',
  'CAMERAS.CHANNELS.CHANNEL_OUTWARD': 'cameras.channels.channel_outward',
  'CAMERAS.CHANNELS.CHANNEL_DMS': 'cameras.channels.channel_dms',
  'CAMERAS.CHANNELS.CHANNEL_EXT1': 'cameras.channels.channel_ext1',
  'CAMERAS.CHANNELS.CHANNEL_EXT2': 'cameras.channels.channel_ext2',
  'CAMERAS.CHANNELS.CHANNEL_EXT3': 'cameras.channels.channel_ext3',
  'CAMERAS.CHANNELS.CHANNEL_EXT4': 'cameras.channels.channel_ext4',
  CONFIGURATION: 'configuration',
  'CONFIGURATION.AREAS': 'configuration.areas',
  'CONFIGURATION.AREAS.AREAS_GROUPS_LIST': 'configuration.areas.areas_groups_list',
  'CONFIGURATION.AREAS.AREAS_GROUPS_LIST.ADD': 'configuration.areas.areas_groups_list.add',
  'CONFIGURATION.AREAS.AREAS_GROUPS_LIST.DELETE': 'configuration.areas.areas_groups_list.delete',
  'CONFIGURATION.AREAS.AREAS_GROUPS_LIST.ARCHIVE': 'configuration.areas.areas_groups_list.archive',
  'CONFIGURATION.AREAS.AREAS_GROUPS_LIST.EDIT': 'configuration.areas.areas_groups_list.edit',
  'CONFIGURATION.AREAS.AREAS_LIST': 'configuration.areas.areas_list',
  'CONFIGURATION.AREAS.AREAS_LIST.ADD': 'configuration.areas.areas_list.add',
  'CONFIGURATION.AREAS.AREAS_LIST.DELETE': 'configuration.areas.areas_list.delete',
  'CONFIGURATION.AREAS.AREAS_LIST.ARCHIVE': 'configuration.areas.areas_list.archive',
  'CONFIGURATION.AREAS.AREAS_LIST.EDIT': 'configuration.areas.areas_list.edit',
  'CONFIGURATION.COMPANY_INFO': 'configuration.company_info',
  'CONFIGURATION.COMPANY_INFO.EDIT': 'configuration.company_info.edit',
  'CONFIGURATION.COMPANY_INFO.STATUS_HISTORY': 'configuration.company_info.status_history',
  'CONFIGURATION.COMPANY_INFO.NOTES_HISTORY': 'configuration.company_info.notes_history',
  'CONFIGURATION.DRIVING_OPTIONS': 'configuration.driving_options',
  'CONFIGURATION.DRIVING_OPTIONS.EDIT': 'configuration.driving_options.edit',
  'CONFIGURATION.FLEET': 'configuration.fleet',
  'CONFIGURATION.FLEET.DEPOT_LIST': 'configuration.fleet.depot_list',
  'CONFIGURATION.FLEET.DEPOT_LIST.ADD': 'configuration.fleet.depot_list.add',
  'CONFIGURATION.FLEET.DEPOT_LIST.DELETE': 'configuration.fleet.depot_list.delete',
  'CONFIGURATION.FLEET.DEPOT_LIST.EDIT': 'configuration.fleet.depot_list.edit',
  'CONFIGURATION.FLEET.VEHICLE_GROUPS_LIST': 'configuration.fleet.vehicle_groups_list',
  'CONFIGURATION.FLEET.VEHICLE_GROUPS_LIST.ADD': 'configuration.fleet.vehicle_groups_list.add',
  'CONFIGURATION.FLEET.VEHICLE_GROUPS_LIST.DELETE': 'configuration.fleet.vehicle_groups_list.delete',
  'CONFIGURATION.FLEET.VEHICLE_GROUPS_LIST.EDIT': 'configuration.fleet.vehicle_groups_list.edit',
  'CONFIGURATION.INTEGRATIONS_LIST': 'configuration.integrations_list',
  'CONFIGURATION.INTEGRATIONS_LIST.EDIT': 'configuration.integrations_list.edit',
  'CONFIGURATION.NOTIFICATIONS_LIST': 'configuration.notifications_list',
  'CONFIGURATION.NOTIFICATIONS_LIST.ADD': 'configuration.notifications_list.add',
  'CONFIGURATION.NOTIFICATIONS_LIST.DELETE': 'configuration.notifications_list.delete',
  'CONFIGURATION.NOTIFICATIONS_LIST.EDIT': 'configuration.notifications_list.edit',
  'CONFIGURATION.SCHEDULED_REPORT_LIST': 'configuration.scheduled_report_list',
  'CONFIGURATION.SCHEDULED_REPORT_LIST.ADD': 'configuration.scheduled_report_list.add',
  'CONFIGURATION.SCHEDULED_REPORT_LIST.DELETE': 'configuration.scheduled_report_list.delete',
  'CONFIGURATION.SCHEDULED_REPORT_LIST.EDIT': 'configuration.scheduled_report_list.edit',
  'CONFIGURATION.TEMPLATES': 'configuration.templates',
  'CONFIGURATION.TEMPLATES.EDIT': 'configuration.templates.edit',
  'CONFIGURATION.USER_GROUP_LIST': 'configuration.user_group_list',
  'CONFIGURATION.USER_GROUP_LIST.ADD': 'configuration.user_group_list.add',
  'CONFIGURATION.USER_GROUP_LIST.DELETE': 'configuration.user_group_list.delete',
  'CONFIGURATION.USER_GROUP_LIST.ARCHIVE': 'configuration.user_group_list.archive',
  'CONFIGURATION.USER_GROUP_LIST.EDIT': 'configuration.user_group_list.edit',
  'CONFIGURATION.USER_GROUP_LIST.VEHICLES': 'configuration.user_group_list.vehicles',
  'CONFIGURATION.USER_GROUP_LIST.AREAS': 'configuration.user_group_list.areas',
  'CONFIGURATION.USER_GROUP_LIST.MODULES': 'configuration.user_group_list.modules',
  'CONFIGURATION.USERS_LIST': 'configuration.users_list',
  'CONFIGURATION.USERS_LIST.ADD': 'configuration.users_list.add',
  'CONFIGURATION.USERS_LIST.BLOCK_USER': 'configuration.users_list.block_user',
  'CONFIGURATION.USERS_LIST.DELETE': 'configuration.users_list.delete',
  'CONFIGURATION.USERS_LIST.ARCHIVE': 'configuration.users_list.archive',
  'CONFIGURATION.USERS_LIST.EDIT': 'configuration.users_list.edit',
  'CONFIGURATION.USERS_LIST.RESET_PASSWORD': 'configuration.users_list.reset_password',
  DEVICE: 'device',
  'DEVICE.DEVICES_LIST': 'device.devices_list',
  'DEVICE.DEVICES_LIST.EDIT': 'device.devices_list.edit',
  'DEVICE.DEVICES_LIST.INSTALL': 'device.devices_list.install',
  'DEVICE.DEVICES_LIST.ARCHIVE': 'device.devices_list.archive',
  'DEVICE.DEVICES_LIST.UNAVAILABLE': 'device.devices_list.unavailable',
  'DEVICE.DEVICES_LIST.RAW_DATA_LOG': 'device.devices_list.raw_data_log',
  'DEVICE.SENSORS_LIST': 'device.sensors_list',
  'DEVICE.SENSORS_LIST.ADD': 'device.sensors_list.add',
  'DEVICE.SENSORS_LIST.DELETE': 'device.sensors_list.delete',
  'DEVICE.SENSORS_LIST.EDIT': 'device.sensors_list.edit',
  DRIVERS: 'drivers',
  'DRIVERS.DOCUMENTS_LIST': 'drivers.documents_list',
  'DRIVERS.DOCUMENTS_LIST.ADD': 'drivers.documents_list.add',
  'DRIVERS.DOCUMENTS_LIST.DELETE': 'drivers.documents_list.delete',
  'DRIVERS.DOCUMENTS_LIST.ARCHIVE': 'drivers.documents_list.archive',
  'DRIVERS.DOCUMENTS_LIST.EDIT': 'drivers.documents_list.edit',
  'DRIVERS.DOCUMENTS_LIST.RECORDS_LIST': 'drivers.documents_list.records_list',
  'DRIVERS.DOCUMENTS_LIST.RECORDS_LIST.ADD': 'drivers.documents_list.records_list.add',
  'DRIVERS.DRIVERS_LIST': 'drivers.drivers_list',
  'DRIVERS.DRIVERS_LIST.ADD': 'drivers.drivers_list.add',
  'DRIVERS.DRIVERS_LIST.BLOCK_USER': 'drivers.drivers_list.block_user',
  'DRIVERS.DRIVERS_LIST.DELETE': 'drivers.drivers_list.delete',
  'DRIVERS.DRIVERS_LIST.ARCHIVE': 'drivers.drivers_list.archive',
  'DRIVERS.DRIVERS_LIST.EDIT': 'drivers.drivers_list.edit',
  'DRIVERS.DRIVERS_LIST.RESET_PASSWORD': 'drivers.drivers_list.reset_password',
  DRIVING_BEHAVIOUR: 'driving_behaviour',
  'DRIVING_BEHAVIOUR.DASHBOARD': 'driving_behaviour.dashboard',
  'DRIVING_BEHAVIOUR.DRIVERS': 'driving_behaviour.drivers',
  'DRIVING_BEHAVIOUR.VEHICLES': 'driving_behaviour.vehicles',
  FLEET: 'fleet',
  'FLEET.DASHBOARD': 'fleet.dashboard',
  'FLEET.DOCUMENTS_LIST': 'fleet.documents_list',
  'FLEET.DOCUMENTS_LIST.ADD': 'fleet.documents_list.add',
  'FLEET.DOCUMENTS_LIST.DELETE': 'fleet.documents_list.delete',
  'FLEET.DOCUMENTS_LIST.ARCHIVE': 'fleet.documents_list.archive',
  'FLEET.DOCUMENTS_LIST.EDIT': 'fleet.documents_list.edit',
  'FLEET.DOCUMENTS_LIST.RECORDS_LIST': 'fleet.documents_list.records_list',
  'FLEET.DOCUMENTS_LIST.RECORDS_LIST.ADD': 'fleet.documents_list.records_list.add',
  'FLEET.INSPECTION_FORMS_LIST': 'fleet.inspection_forms_list',
  'FLEET.INSPECTION_FORMS_LIST.ADD': 'fleet.inspection_forms_list.add',
  'FLEET.INSPECTION_FORMS_LIST.DELETE': 'fleet.inspection_forms_list.delete',
  'FLEET.INSPECTION_FORMS_LIST.EDIT': 'fleet.inspection_forms_list.edit',
  'FLEET.SERVICE_REMINDERS_LIST': 'fleet.service_reminders_list',
  'FLEET.SERVICE_REMINDERS_LIST.ADD': 'fleet.service_reminders_list.add',
  'FLEET.SERVICE_REMINDERS_LIST.DELETE': 'fleet.service_reminders_list.delete',
  'FLEET.SERVICE_REMINDERS_LIST.ARCHIVE': 'fleet.service_reminders_list.archive',
  'FLEET.SERVICE_REMINDERS_LIST.EDIT': 'fleet.service_reminders_list.edit',
  'FLEET.SERVICE_REMINDERS_LIST.SERVICE_RECORD_LIST': 'fleet.service_reminders_list.service_record_list',
  'FLEET.SERVICE_REMINDERS_LIST.SERVICE_RECORD_LIST.ADD': 'fleet.service_reminders_list.service_record_list.add',
  'FLEET.SERVICE_REMINDERS_LIST.SERVICE_RECORD_LIST.EDIT': 'fleet.service_reminders_list.service_record_list.edit',
  'FLEET.SERVICE_REMINDERS_LIST.SERVICE_RECORD_LIST.DELETE': 'fleet.service_reminders_list.service_record_list.delete',
  'FLEET.VEHICLE_LIST': 'fleet.vehicle_list',
  'FLEET.VEHICLE_LIST.ADD': 'fleet.vehicle_list.add',
  'FLEET.VEHICLE_LIST.DELETE': 'fleet.vehicle_list.delete',
  'FLEET.VEHICLE_LIST.EDIT': 'fleet.vehicle_list.edit',
  'FLEET.VEHICLE_LIST.ARCHIVE': 'fleet.vehicle_list.archive',
  'FLEET.REPAIR_COSTS_LIST': 'fleet.repair_costs_list',
  'FLEET.REPAIR_COSTS_LIST.ADD': 'fleet.repair_costs_list.add',
  'FLEET.REPAIR_COSTS_LIST.DELETE': 'fleet.repair_costs_list.delete',
  'FLEET.REPAIR_COSTS_LIST.EDIT': 'fleet.repair_costs_list.edit',
  FUEL: 'fuel',
  'FUEL.IMPORT_DATA': 'fuel.import_data',
  'FUEL.RECORDS': 'fuel.records',
  'FUEL.SUMMARY': 'fuel.summary',
  'FUEL.FUEL_RECORD_UPDATE': 'fuel.fuel_record_update',
  MAP: 'map',
  'MAP.AREAS': 'map.areas',
  'MAP.ASSETS': 'map.assets',
  'MAP.DRIVERS': 'map.drivers',
  'MAP.VEHICLE': 'map.vehicle',
  'MAP.VEHICLE.SHARE_TRACKING_LINK': 'map.vehicle.share_tracking_link',
  MESSENGER: 'messenger',
  'MESSENGER.SHOW_ALL': 'messenger.show_all',
  'MESSENGER.CREATE_CHAT': 'messenger.create_chat',
  REPORTS: 'reports',
  SUPPORT: 'support',
  'SUPPORT.CONTACT_US': 'support.contact_us',
  'SUPPORT.SUBMIT_TICKET': 'support.submit_ticket',
  'SUPPORT.KNOWLEDGE_BASE': 'support.knowledge_base',
}
